import { useQuery } from '@apollo/client';
import { Button, Col, Row } from 'antd';
import classNames from 'classnames';
import {
  groupBy,
  includes,
  isObject,
  keys,
  noop,
  range,
  some,
  sortBy,
  toPairs,
} from 'lodash-es';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { graphQLToFormAddressBookEntry } from '../../app/data/addressBookConversions';
import {
  ADDRESS_BOOK_ENTRIES_QUERY,
  ADDRESS_BOOK_ENTRY_FORM_AUTOFILL_QUERY,
  ADDRESS_BOOK_ENTRY_FORM_GET_OPTIONS_QUERY,
  ADDRESS_BOOK_ENTRY_FORM_SCHEMA_QUERY,
  VALIDATE_ADDRESS_BOOK_ENTRY_FORM_FIELD_QUERY,
} from '../../app/graphql/addressBookQueries';
import { formatCodeValue } from '../../common/utils/formatUtils';
import { extractGraphqlEntity } from '../../common/utils/graphqlUtils';
import { normalizeString } from '../../common/utils/stringUtils';
import { FAIcon } from '../../components/adapters/fontAwesomeAdapters';
import IconButton from '../../components/buttons/IconButton';
import { renderDataStateIndicator } from '../../components/data/dataStateHandlers';
import { SimpleConfirmDialog } from '../../components/dialogs/SimpleConfirmDialog';
import { DRAWER_MEDIA_MAPPER } from '../../components/drawers/drawerCommon';
import { FormItemInputText } from '../../components/forms/basicFormElements';
import {
  DynamicFormDependenciesProvider,
  useDynamicOriginalValueDependency,
} from '../../components/forms/dynamic/dynamicFormDependencies';
import { DynamicFormSchemaProvider } from '../../components/forms/dynamic/dynamicFormSchema';
import { useFormContext } from '../../components/forms/forms';
import { useLayoutElementsStyles } from '../../components/layout/layoutElements.styles';
import TemplatesFormDrawer from '../../components/popups/templates/TemplatesFormDrawer';
import { ResponsiveOverrideSubtree } from '../../components/responsive/ResponsiveOverrideSubtree';
import { BlockLink } from '../../components/typography';
import { SavedShipmentProvider } from '../../forms/shipments/newShipment/SavedShipmentProvider';
import { JobLocationCards } from '../../forms/shipments/newShipment/newShipmentElements';
import { cssVariables } from '../../styles/cssVariables';
import { pxToNumber } from '../../utils/cssUtils';
import { AddressBookHeaderRightContent } from './AddressBookHeader';
import { AddressBookList } from './AddressBookList';
import { useAddressBookLogic } from './useAddressBook';
import { useAddressBookStyles } from './addressBook.styles';

const NON_ALPHA = '#';
const ALPHABET = [
  NON_ALPHA,
  ...range('a'.charCodeAt(), 'z'.charCodeAt() + 1).map(charCode =>
    String.fromCharCode(charCode)
  ),
];

function AddressBookDrawerListLetterShortcuts({
  letters,
  isFormVisible,
  scrollToLetter,
}) {
  const [open, setOpen] = useState(true);
  const { styles } = useAddressBookStyles();

  return (
    <div
      className={classNames(styles.letterShortcuts, {
        open: open && !isFormVisible,
        closed: isFormVisible,
      })}
      data-subject="address-book-letter-shortcuts"
      data-role="list"
    >
      <IconButton
        icon="arrows-alt-h"
        className="hide-lg-and-bigger"
        onClick={() => setOpen(!open)}
      />
      {ALPHABET.map(letter => {
        const present = !letters || includes(letters, letter);
        return (
          <BlockLink
            key={letter}
            className={classNames({ disabled: !present })}
            onClick={present ? () => scrollToLetter(letter) : noop}
          >
            {letter}
          </BlockLink>
        );
      })}
    </div>
  );
}

function makeAddressBookSections(entries) {
  if (!entries) {
    return [];
  }
  const sortedEntries = sortBy(entries, 'companyName');
  const entriesByLetter = groupBy(sortedEntries, entry => {
    const firstLetter = normalizeString(entry.companyName).slice(0, 1);
    return /[a-z]/.test(firstLetter) ? firstLetter : NON_ALPHA;
  });
  const sections = sortBy(toPairs(entriesByLetter), ([letter]) => letter);
  const sectionsWithIndices = sections.map(([letter, subentries]) => [
    letter,
    subentries.map((entry, indexInSection) => ({
      data: entry,
      index: indexInSection,
    })),
  ]);
  return { sections: sectionsWithIndices, letters: keys(entriesByLetter) };
}

export function AddressBookNoDataDialog({ onAdd, isOpen, onCancel, readOnly }) {
  const { styles } = useAddressBookStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  return (
    <SimpleConfirmDialog
      textId="addressBook.warning.noData"
      className={classNames(styles.noDataDialog, 'size-sm')}
      open={isOpen}
    >
      <Row
        align="top"
        justify={readOnly ? 'center' : 'start'}
        gutter={pxToNumber(cssVariables.spaceNorm)}
      >
        <Col>
          <Button onClick={onCancel} ghost>
            <FormattedMessage id="buttons.cancel" />
          </Button>
        </Col>
        {!readOnly && (
          <Col className={layoutStyles.flex1}>
            <Button className="btn-blue" onClick={onAdd}>
              <FAIcon icon="plus" />
              <span>
                <FormattedMessage id="addressBook.buttons.addContact" />
              </span>
            </Button>
          </Col>
        )}
      </Row>
    </SimpleConfirmDialog>
  );
}

function filterAddressEntries(entries, searchValue) {
  return entries.filter(entry => {
    if (!searchValue) {
      return true;
    }
    const searchedFields = [
      entry.companyName,
      entry.address.addressLine1,
      entry.address.postalCode,
      formatCodeValue(entry.address.city),
      formatCodeValue(entry.address.stateProvince),
      formatCodeValue(entry.address.country),
      entry.keyword,
    ];
    return some(
      searchedFields,
      fieldValue =>
        normalizeString(fieldValue).indexOf(normalizeString(searchValue)) > -1
    );
  });
}

export function AddressBookDrawerListContent({
  onEdit,
  onApply,
  active,
  onDelete,
  removeDialogClassName,
  removeText,
  removeTextId,
  removeSuccessMessageId,
  isFormVisible,
  openNoDataDialog,
  searchValue,
  readOnly,
  countryCodeRestrict,
  namePrefix,
  open,
  justClosedAddForm,
  setJustClosedAddForm,
  onEntriesLoaded,
}) {
  const listRef = useRef();
  const accountNumber = useDynamicOriginalValueDependency('accountNumber');
  const { data, loading, error } = useQuery(ADDRESS_BOOK_ENTRIES_QUERY, {
    variables: { accountNumber, countryCode: countryCodeRestrict },
    skip: !accountNumber,
  });
  const indicator = renderDataStateIndicator({ data, loading, error });
  const allEntries = data && extractGraphqlEntity(data).data;
  const entries = allEntries && filterAddressEntries(allEntries, searchValue);
  const { sections, letters } = useMemo(
    () => makeAddressBookSections(entries),
    [entries]
  );

  const allEntriesLength = allEntries?.length || 0;
  useEffect(() => {
    if (!loading && onEntriesLoaded) {
      onEntriesLoaded(allEntriesLength);
    }
  }, [allEntriesLength, loading, onEntriesLoaded]);

  useEffect(() => {
    if (allEntriesLength > 0 && setJustClosedAddForm) {
      setJustClosedAddForm(false);
    }
  }, [allEntriesLength, setJustClosedAddForm]);
  const shouldOpenNoDataDialog =
    !loading &&
    allEntriesLength === 0 &&
    !isFormVisible &&
    open &&
    !justClosedAddForm;

  useEffect(() => {
    // Open no data dialog anytime loading has been finished, there is no data
    // and this mode is visible
    if (shouldOpenNoDataDialog) {
      openNoDataDialog();
    }
  }, [shouldOpenNoDataDialog, openNoDataDialog]);

  return (
    indicator || (
      <>
        <AddressBookDrawerListLetterShortcuts
          letters={letters}
          isFormVisible={isFormVisible}
          scrollToLetter={letter => listRef.current.scrollToLetter(letter)}
        />
        <AddressBookList
          sections={sections}
          active={active}
          onEdit={onEdit}
          onApply={onApply}
          onDelete={onDelete}
          removeDialogClassName={removeDialogClassName}
          removeText={removeText}
          removeTextId={removeTextId}
          removeSuccessMessageId={removeSuccessMessageId}
          ref={listRef}
          readOnly={readOnly}
          namePrefix={namePrefix}
        />
      </>
    )
  );
}

function AddressBookFormContent() {
  const { values } = useFormContext();
  const accountNumber = useDynamicOriginalValueDependency('accountNumber');

  return (
    <ResponsiveOverrideSubtree mapper={DRAWER_MEDIA_MAPPER}>
      <DynamicFormSchemaProvider
        query={ADDRESS_BOOK_ENTRY_FORM_SCHEMA_QUERY}
        optionsQuery={ADDRESS_BOOK_ENTRY_FORM_GET_OPTIONS_QUERY}
        fieldValidationQuery={VALIDATE_ADDRESS_BOOK_ENTRY_FORM_FIELD_QUERY}
        autofillQuery={ADDRESS_BOOK_ENTRY_FORM_AUTOFILL_QUERY}
        variables={{ accountNumber }}
        skip={!accountNumber}
        queryName="addressBookFieldValidation"
        clearCache
      >
        <DynamicFormDependenciesProvider values={values}>
          <div className="spaces-vert-norm2">
            <FormItemInputText
              name="keyword"
              schemaName="keyword"
              labelId="addressBook.keyword"
            />
            <JobLocationCards
              firstTitleId="addressBook.countrySection.label"
              secondTitleId="addressBook.shipmentContactInformation"
              enableAddressLookup={{ webResults: true }}
            />
          </div>
        </DynamicFormDependenciesProvider>
      </DynamicFormSchemaProvider>
    </ResponsiveOverrideSubtree>
  );
}

export function AddressBookDrawerBase({
  open,
  data,
  setData,
  onClose,
  readOnly,
  isFormVisible,
  onCancel,
  onSave,
  REMOVE_PROPS,
  searchValue,
  searchOnChange,
  getCreateInitialValues,
  getEditInitialValues,
  children,
  isHeaderVisible = true,
}) {
  const { styles } = useAddressBookStyles();
  return (
    <TemplatesFormDrawer
      destroyOnHidden={false}
      open={open}
      data={data}
      readOnly={readOnly}
      setData={setData}
      onClose={onClose}
      onCancel={onCancel}
      titleId="addressBook.title"
      headerRightContent={
        isHeaderVisible && (
          <div
            className={classNames(styles.headerRightContent, {
              disabled: isFormVisible,
            })}
          >
            <AddressBookHeaderRightContent
              searchValue={searchValue}
              searchOnChange={searchOnChange}
              setData={setData}
              readOnly={readOnly}
            />
          </div>
        )
      }
      onSave={onSave}
      formClassName={styles.form}
      dividerClassName={styles.formDivider}
      formName="addressBookEntry"
      formComponent={AddressBookFormContent}
      createTitleId="addressBook.add.title"
      createSuccessMessageId="addressBook.create.success"
      editTitleId="addressBook.edit.title"
      editSuccessMessageId="addressBook.edit.success"
      getCreateInitialValues={getCreateInitialValues}
      getEditInitialValues={getEditInitialValues}
      {...REMOVE_PROPS}
    >
      {children}
    </TemplatesFormDrawer>
  );
}

export default function AddressBookDrawer({
  open,
  data,
  openDrawer,
  setData,
  onClose,
  readOnly,
  countryCodeRestrict,
  namePrefix,
}) {
  const { values } = useFormContext();
  const accountNumber = useDynamicOriginalValueDependency('accountNumber');
  const isFormVisible = data?.editing || data?.adding;

  const userHasInteractedRef = useRef(false);
  const [justClosedAddForm, setJustClosedAddForm] = useState(false);
  const [entriesCount, setEntriesCount] = useState(0);
  const prevIsFormVisibleRef = useRef(isFormVisible);

  useEffect(() => {
    const wasFormVisible = prevIsFormVisibleRef.current;
    const isFormNowClosed = wasFormVisible && !isFormVisible;
    if (isFormNowClosed) {
      userHasInteractedRef.current = true;
      setJustClosedAddForm(true);
    }
    prevIsFormVisibleRef.current = isFormVisible;
  }, [isFormVisible]);

  const {
    activeItem,
    setActiveItem,
    onCancel: originalOnCancel,
    onSave,
    REMOVE_PROPS,
    searchValue,
    searchOnChange,
    noDataDialog,
    openNoDataDialog,
    getEditInitialValues,
  } = useAddressBookLogic(accountNumber, setData, onClose);

  const onCancel = () => {
    userHasInteractedRef.current = true;
    setJustClosedAddForm(true);
    originalOnCancel();
    if (entriesCount === 0) {
      onClose();
    }
  };

  const openNoDataDialogSafe = () => {
    if (!userHasInteractedRef.current) {
      openNoDataDialog();
    }
  };

  useEffect(() => {
    if (!open) {
      userHasInteractedRef.current = false;
      setJustClosedAddForm(false);
    }
  }, [open]);

  const getCreateInitialValues = () =>
    isObject(data?.adding)
      ? graphQLToFormAddressBookEntry(data.adding)
      : values;

  const addressBookDrawerBaseProps = {
    open,
    data,
    setData,
    onClose,
    readOnly,
    isFormVisible,
    onCancel,
    onSave,
    REMOVE_PROPS,
    searchValue,
    searchOnChange,
    getEditInitialValues,
  };

  return (
    <SavedShipmentProvider enabled={false}>
      <AddressBookDrawerBase
        {...addressBookDrawerBaseProps}
        getCreateInitialValues={getCreateInitialValues}
      >
        <AddressBookDrawerListContent
          onEdit={val => setData({ editing: val })}
          onApply={({ id }) => {
            onClose();
            setActiveItem({ id });
          }}
          onClose={onClose}
          active={activeItem}
          isFormVisible={isFormVisible}
          openNoDataDialog={openNoDataDialogSafe}
          searchValue={searchValue}
          readOnly={readOnly}
          countryCodeRestrict={countryCodeRestrict}
          namePrefix={namePrefix}
          open={open}
          justClosedAddForm={justClosedAddForm}
          setJustClosedAddForm={setJustClosedAddForm}
          onEntriesLoaded={setEntriesCount}
          {...REMOVE_PROPS}
        />
      </AddressBookDrawerBase>
      <AddressBookNoDataDialog
        onAdd={() => {
          openDrawer({ adding: true });
          noDataDialog.close();
        }}
        isOpen={noDataDialog.isOpen}
        onCancel={noDataDialog.close}
        readOnly={readOnly}
      />
    </SavedShipmentProvider>
  );
}
